import { useEffect, useMemo, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import api from "../../../../Hooks/axiosApi";
import { useSafeNavigate } from "../../../../Hooks/useSafeNavigate";
import { Lender, LoanProduct, getProductTypeLabel, normalizeLender, normalizeLoanProduct } from "./loanProductUtils";


const getProductInterestRange = (product: LoanProduct) => {
  let fromVal: number | null = null;
  let toVal: number | null = null;
  if (!product.loanDetailsJson) return { from: null, to: null };
  try {
    const parsed = JSON.parse(product.loanDetailsJson);
    const config = parsed?.productTypeConfig;
    if (product.productType === "Property" && config?.property) {
      const bands = config.property.lvrInterestRates || [];
      const froms: number[] = [];
      const tos: number[] = [];
      bands.forEach((b: any) => {
        const keys = [
          "residential1stFrom", "residential2ndFrom", "commercialFrom", "farmRuralFrom",
          "residential1stTo", "residential2ndTo", "commercialTo", "farmRuralTo"
        ];
        keys.forEach(k => {
          const v = parseFloat(b[k]);
          if (!isNaN(v) && v > 0) {
            if (k.endsWith("From")) froms.push(v);
            if (k.endsWith("To")) tos.push(v);
          }
        });
      });
      if (froms.length > 0) fromVal = Math.min(...froms);
      if (tos.length > 0) toVal = Math.max(...tos);
    } else if (product.productType === "Asset - Secured" && config?.assetSecured) {
      const rates = config.assetSecured.interestRates || [];
      if (rates[0]) {
        const f = parseFloat(rates[0].from);
        const t = parseFloat(rates[0].to);
        if (!isNaN(f) && f > 0) fromVal = f;
        if (!isNaN(t) && t > 0) toVal = t;
      }
    } else if (product.productType === "Asset - Unsecured" && config?.assetUnsecured) {
      const rates = config.assetUnsecured.interestRates || [];
      if (rates[0]) {
        const f = parseFloat(rates[0].from);
        const t = parseFloat(rates[0].to);
        if (!isNaN(f) && f > 0) fromVal = f;
        if (!isNaN(t) && t > 0) toVal = t;
      }
    }
  } catch (e) {
    console.error(e);
  }
  return { from: fromVal, to: toVal };
};

const calculate100kRepayment = (product: LoanProduct, interestFrom: number | null): string => {
  if (interestFrom === null || interestFrom <= 0) return "-";
  
  const principal = 100000;
  let termMonths = 60; // default fallback
  let feesAndCharges: any[] = [];
  let repaymentType = "P&I"; // default
  
  try {
    if (product.loanDetailsJson) {
      const parsed = JSON.parse(product.loanDetailsJson);
      const config = parsed?.productTypeConfig;
      
      if (product.productType === "Property" && config?.property) {
        termMonths = parseInt(config.property.loanTermMonthsMax) || 12;
        feesAndCharges = config.property.feesAndCharges || [];
        if (config.property.repaymentType && config.property.repaymentType.includes("Interest Only")) {
          repaymentType = "Interest Only";
        }
      } else if (product.productType === "Asset - Secured" && config?.assetSecured) {
        termMonths = parseInt(config.assetSecured.loanTermMonthsMax) || 60;
        feesAndCharges = config.assetSecured.feesAndCharges || [];
      } else if (product.productType === "Asset - Unsecured" && config?.assetUnsecured) {
        termMonths = parseInt(config.assetUnsecured.loanTermMonthsMax) || 12;
        feesAndCharges = config.assetUnsecured.feesAndCharges || [];
      }
    }
  } catch (e) {
    console.error(e);
  }
  
  // Calculate fees
  let totalFees = 0;
  if (Array.isArray(feesAndCharges)) {
    feesAndCharges.forEach((fee: any) => {
      const amount = Number(String(fee.amount || "").replace(/[$,\s]/g, ""));
      const percent = Number(String(fee.percent || "").replace(/[%,\s]/g, ""));
      if (amount) {
        totalFees += amount;
      } else if (percent) {
        totalFees += (principal * percent) / 100;
      }
    });
  }
  
  // Calculate interest component
  const r = (interestFrom / 100) / 12;
  let interestComponent = 0;
  
  if (repaymentType === "Interest Only") {
    interestComponent = principal * (interestFrom / 100) * (termMonths / 12);
  } else {
    // P&I
    if (r === 0) {
      interestComponent = 0;
    } else {
      const monthly = (principal * r * Math.pow(1 + r, termMonths)) / (Math.pow(1 + r, termMonths) - 1);
      interestComponent = (monthly * termMonths) - principal;
    }
  }
  
  const totalRepaid = principal + interestComponent + totalFees;
  return new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(totalRepaid);
};

const CRMLoanProducts = () => {
  const navigate = useSafeNavigate();
  const [products, setProducts] = useState<LoanProduct[]>([]);
  const [lenders, setLenders] = useState<Lender[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [deleting, setDeleting] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<LoanProduct | null>(null);

  const lenderNameMap = useMemo(() => {
    const mapping: Record<number, string> = {};
    lenders.forEach((lender) => {
      mapping[lender.lenderId] = lender.lenderName;
    });
    return mapping;
  }, [lenders]);

  const fetchData = async () => {
    setLoading(true);
    setError("");

    try {


      const [productsResponse, lendersResponse] = await Promise.all([
        api.get("/api/Lenders/Getloan-products"),
        api.get("/api/Lenders/Getlenders"),
      ]);

      const productsData = Array.isArray(productsResponse?.data) ? productsResponse.data : [];
      const lendersData = Array.isArray(lendersResponse?.data) ? lendersResponse.data : [];

      const normalizedLenders = lendersData.map(normalizeLender).filter((item: Lender) => item.lenderId > 0);
      normalizedLenders.sort((a, b) => (a.lenderName || "").localeCompare(b.lenderName || ""));

      const mapping: Record<number, string> = {};
      normalizedLenders.forEach((lender) => {
        mapping[lender.lenderId] = lender.lenderName;
      });

      const normalizedProducts = productsData
        .map(normalizeLoanProduct)
        .filter((item: LoanProduct) => item.loanProductId > 0 && !item.isDeleted);
        
      normalizedProducts.sort((a, b) => {
        const lenderA = mapping[a.lenderId] || a.lenderName || "";
        const lenderB = mapping[b.lenderId] || b.lenderName || "";
        const lenderRes = lenderA.localeCompare(lenderB);
        if (lenderRes !== 0) return lenderRes;
        return (a.productName || "").localeCompare(b.productName || "");
      });

      setProducts(normalizedProducts);
      setLenders(normalizedLenders);
    } catch (apiError: any) {
      setError(apiError?.response?.data?.message || "Failed to load loan products.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async () => {
    if (!selectedProduct?.loanProductId) {
      setError("Cannot delete product because id was not found.");
      toast.error("Cannot delete product because id was not found.");
      return;
    }

    setDeleting(true);
    setError("");
    try {
      await api.delete(`/api/Lenders/Deleteloan-products/${selectedProduct.loanProductId}`);
      toast.success("Loan product deleted successfully.");
      setDeleteModal(false);
      setSelectedProduct(null);
      await fetchData();
    } catch (apiError: any) {
      const message = apiError?.response?.data?.message || "Failed to delete loan product.";
      setError(message);
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="px-4 pt-4"
      style={{
        minHeight: "500px",
        boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
        backgroundColor: "#f9f9f9",
        borderRadius: "8px",
      }}
    >
      <div
        style={{
          borderRadius: "8px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          padding: "16px",
          backgroundColor: "#fff",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3 className="f-w-900 m-0 text-primary">Loan Products</h3>
           <span style={{ color: "#1e8e3e", fontWeight: "bold", fontSize: "14px" }}>
              TCOB here is calculated using a loan amount of $100,000 over each product's maximum term.
            </span>
          <Button color="primary" onClick={() => navigate("/AddLoanProduct")}>
            Add Loan Product
          </Button>
        </div>

        {!!error && (
          <div className="alert alert-danger py-2 mb-3" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped table-bordered align-middle">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Lender</th>
                  <th>Product Name</th>
                  <th>Product Type</th>
                  <th>Interest From</th>
                  <th>Interest To</th>
                  <th>$ 100k Loan</th>
                  <th style={{ width: "170px" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center">
                      No loan products found.
                    </td>
                  </tr>
                ) : (
                  products.map((product) => {
                    const { from, to } = getProductInterestRange(product);
                    const interestFromStr = from !== null ? `${from.toFixed(2)}%` : "-";
                    const interestToStr = to !== null ? `${to.toFixed(2)}%` : "-";
                    const repayment100k = calculate100kRepayment(product, from);

                    return (
                      <tr key={product.loanProductId}>
                        <td>{product.loanProductId}</td>
                        <td>{lenderNameMap[product.lenderId] || product.lenderName || product.lenderId}</td>
                        <td>{product.productName}</td>
                        <td>{getProductTypeLabel(product.productType)}</td>
                        <td>{product.InterestFrom && !["0", "0.00%", "-"].includes(product.InterestFrom.trim()) ? product.InterestFrom : "-"}</td>
                        <td>{product.InterestTo && !["0", "0.00%", "-"].includes(product.InterestTo.trim()) ? product.InterestTo : "-"}</td>
                        <td>{product.LoanAmountMin}</td>
                        <td>
                          <div className="d-flex gap-2">
                            <Button
                              type="button"
                              color="primary"
                              size="sm"
                              onClick={() => navigate(`/EditLoanProduct/${product.loanProductId}`)}
                            >
                              Edit
                            </Button>
                            <Button
                              type="button"
                              color="danger"
                              size="sm"
                              onClick={() => {
                                setSelectedProduct(product);
                                setDeleteModal(true);
                              }}
                              disabled={deleting}
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)}>
        <ModalHeader toggle={() => setDeleteModal(!deleteModal)}>Delete Loan Product</ModalHeader>
        <ModalBody>
          Are you sure you want to delete <strong>{selectedProduct?.productName || "this loan product"}</strong>?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CRMLoanProducts;


